import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, Sparkles, ArrowRight } from "lucide-react";
import { MARKET_META } from "../lib/api";
import { SEO_MARKETS_BY_KEY, resultTodayPath } from "../lib/seoMarkets";

const formatDDMMYYYY = (iso) => {
    if (!iso) return "";
    const s = String(iso).slice(0, 10);
    const [y, m, d] = s.split("-");
    if (!y) return iso;
    return `${d}.${m}.${y}`;
};

const numOrPending = (val) => {
    if (val === null || val === undefined || val === "") {
        return { text: "", pending: true };
    }
    return { text: String(val).padStart(2, "0"), pending: false };
};

// ---- Live-window helpers ----------------------------------------------------

const timeToMinutes = (t) => {
    // "10:10 AM" | "04:15 PM" | "12:25 AM" → minutes since midnight
    if (!t) return null;
    const m = String(t).trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!m) return null;
    let h = parseInt(m[1], 10);
    const min = parseInt(m[2], 10);
    const ap = m[3].toUpperCase();
    if (ap === "AM") {
        if (h === 12) h = 0;
    } else {
        if (h !== 12) h += 12;
    }
    return h * 60 + min;
};

const nowISTMinutes = () => {
    const s = new Date().toLocaleTimeString("en-GB", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: false });
    const [h, m] = s.split(":").map(Number);
    return h * 60 + m;
};

// LIVE if within [-15, +30] min of either round.
const computeLive = (frTime, srTime) => {
    const now = nowISTMinutes();
    const fr = timeToMinutes(frTime);
    let sr = timeToMinutes(srTime);
    if (fr == null || sr == null) return { isLive: false, phase: null };

    // Handle S/R after midnight (e.g. 12:25 AM). If S/R < F/R, add a day.
    if (sr < fr) sr += 24 * 60;

    // Normalise "now" — if we're before dawn (0-6 AM) but the S/R is > 24h, treat
    // "now" as +24h so late-night comparison works.
    let n = now;
    if (n < 60 * 6 && sr > 24 * 60) n += 24 * 60;

    const inWindow = (t) => n >= t - 15 && n <= t + 30;
    if (inWindow(fr)) return { isLive: true, phase: "F/R" };
    if (inWindow(sr)) return { isLive: true, phase: "S/R" };
    return { isLive: false, phase: null };
};

/**
 * Compact market result card with an auto "LIVE NOW" highlight when the current
 * IST time is inside a ±window of the market's F/R or S/R declaration slot.
 */
export const MarketCard = ({ marketKey, result, date, onRefresh }) => {
    const meta = MARKET_META[marketKey] || {
        name: marketKey,
        fr_time: "-",
        sr_time: "-",
        accent: "#1E3A8A",
        emoji: "🎯",
    };
    const fr = numOrPending(result?.fr);
    const sr = numOrPending(result?.sr);
    const headerTextColor = meta.headerTextColor || "#FFFFFF";

    // Live status re-evaluated every 30s
    const [live, setLive] = useState(() => computeLive(meta.fr_time, meta.sr_time));
    useEffect(() => {
        const id = setInterval(() => setLive(computeLive(meta.fr_time, meta.sr_time)), 30 * 1000);
        return () => clearInterval(id);
    }, [meta.fr_time, meta.sr_time]);

    // SEO landing page path for this market
    const seoMeta = SEO_MARKETS_BY_KEY[marketKey];
    const detailPath = seoMeta ? resultTodayPath(seoMeta.urlPrefix) : null;

    return (
        <div
            className={`rounded-lg overflow-hidden bg-white border shadow-sm transition-shadow ${
                live.isLive
                    ? "border-red-400 ring-2 ring-red-300/60 shadow-[0_0_0_4px_rgba(239,68,68,0.10)] card-live"
                    : "border-slate-200"
            }`}
            data-testid={`market-card-${marketKey}`}
            data-live={live.isLive ? live.phase : "off"}
        >
            {/* Colored header */}
            <div
                className="px-2 py-1.5 flex items-center justify-center gap-1.5 relative"
                style={{ background: meta.accent, color: headerTextColor }}
            >
                {meta.special && (
                    <span
                        className="absolute -top-1.5 right-1.5 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[8px] font-bold bg-yellow-400 text-purple-900 uppercase tracking-wider shadow-md ring-2 ring-white"
                        data-testid={`special-badge-${marketKey}`}
                    >
                        <Sparkles className="h-2 w-2" /> Special
                    </span>
                )}
                {live.isLive && (
                    <span
                        className="absolute -top-1.5 left-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[8px] font-bold bg-red-500 text-white uppercase tracking-wider shadow-md ring-2 ring-white"
                        data-testid={`live-badge-${marketKey}`}
                        title={`${live.phase} declaration window is open`}
                    >
                        <span className="h-1 w-1 rounded-full bg-white live-dot" />
                        Live · {live.phase}
                    </span>
                )}
                <span className="text-sm leading-none" aria-hidden="true">{meta.emoji}</span>
                {detailPath ? (
                    <Link
                        to={detailPath}
                        className="font-display font-bold text-[11px] sm:text-xs tracking-tight text-center hover:underline leading-tight"
                        data-testid={`market-title-link-${marketKey}`}
                    >
                        {meta.name}
                        <span className="hidden xs:inline"> - </span>
                        <span className="block sm:inline text-[10px] sm:text-xs opacity-90" data-testid={`market-date-${marketKey}`}>{formatDDMMYYYY(date)}</span>
                    </Link>
                ) : (
                    <h3 className="font-display font-bold text-[11px] sm:text-xs tracking-tight text-center leading-tight">
                        {meta.name}
                        <span className="block sm:inline text-[10px] sm:text-xs opacity-90" data-testid={`market-date-${marketKey}`}>{formatDDMMYYYY(date)}</span>
                    </h3>
                )}
            </div>

            {/* Column headers */}
            <div className="grid grid-cols-2 bg-slate-100/70 border-b border-slate-200">
                <div className={`px-1 py-1 text-center text-[10px] sm:text-xs font-semibold border-r border-slate-200 ${live.phase === "F/R" ? "bg-red-50 text-red-800" : "text-blue-900"}`}>
                    F/R <span className="opacity-80">({meta.fr_time})</span>
                </div>
                <div className={`px-1 py-1 text-center text-[10px] sm:text-xs font-semibold ${live.phase === "S/R" ? "bg-red-50 text-red-800" : "text-blue-900"}`}>
                    S/R <span className="opacity-80">({meta.sr_time})</span>
                </div>
            </div>

            {/* Numbers */}
            <div className="grid grid-cols-2 divide-x divide-slate-200">
                <div className="px-1 py-2 text-center">
                    {fr.pending ? (
                        <PendingSpinner testid={`result-fr-${marketKey}`} live={live.phase === "F/R"} />
                    ) : (
                        <div
                            className="result-digit text-2xl sm:text-3xl text-blue-900 leading-none"
                            data-testid={`result-fr-${marketKey}`}
                        >
                            {fr.text}
                        </div>
                    )}
                </div>
                <div className="px-1 py-2 text-center">
                    {sr.pending ? (
                        <PendingSpinner testid={`result-sr-${marketKey}`} live={live.phase === "S/R"} />
                    ) : (
                        <div
                            className="result-digit text-2xl sm:text-3xl text-blue-900 leading-none"
                            data-testid={`result-sr-${marketKey}`}
                        >
                            {sr.text}
                        </div>
                    )}
                </div>
            </div>

            {/* Footer: refresh + updated time + view details */}
            <div className="border-t border-slate-200 bg-slate-50 px-2 py-1 flex items-center justify-between gap-1">
                <span className="text-[9px] text-slate-500 truncate">
                    {result?.updated_at
                        ? `${new Date(result.updated_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
                        : "Awaiting"}
                </span>
                <div className="flex items-center gap-2">
                    {detailPath && (
                        <Link
                            to={detailPath}
                            data-testid={`view-details-${marketKey}`}
                            className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-blue-900 hover:text-blue-700 transition-colors"
                        >
                            Details <ArrowRight className="h-2.5 w-2.5" />
                        </Link>
                    )}
                    <button
                        onClick={onRefresh}
                        data-testid={`refresh-btn-${marketKey}`}
                        className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-blue-900 hover:text-blue-700 transition-colors"
                        aria-label="Refresh"
                    >
                        <RefreshCw className="h-2.5 w-2.5" />
                    </button>
                </div>
            </div>
        </div>
    );
};

const PendingSpinner = ({ testid, live = false }) => (
    <div className="flex items-center justify-center" data-testid={testid}>
        <span className="relative inline-flex h-8 w-8 sm:h-9 sm:w-9">
            <span className={`absolute inset-0 rounded-full border-[3px] ${live ? "border-red-100" : "border-blue-100"}`} />
            <span className={`absolute inset-0 rounded-full border-[3px] border-t-transparent animate-spin ${live ? "border-red-500" : "border-blue-800"}`} />
        </span>
    </div>
);
